import { useState, useEffect, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { X } from "lucide-react"
import Chat from "../../features/chat/components/Chat"

export default function FloatingChat() {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()

  // Hide on /chat route
  if (location.pathname === "/chat") return null

  return (
    <>
      {!isOpen && (
        <HeartButton onClick={() => setIsOpen(true)} />
      )}
      {isOpen && (
        <ChatPanel onClose={() => setIsOpen(false)} />
      )}
    </>
  )
}

// ─── Heart Point Cloud Button ───────────────────────────────────────────────

function HeartButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number>(0)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipFaded, setTooltipFaded] = useState(false)
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const size = 70
    canvas.width = size * dpr
    canvas.height = size * dpr
    canvas.style.width = size + "px"
    canvas.style.height = size + "px"
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.scale(dpr, dpr)

    // Generate 200 3D heart-shaped points
    const points: { x: number; y: number; z: number; hue: number }[] = []
    for (let i = 0; i < 200; i++) {
      const t = (i / 200) * Math.PI * 2
      const x = 16 * Math.pow(Math.sin(t), 3)
      const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t))
      const z = (Math.random() - 0.5) * 8
      points.push({
        x: x + (Math.random() - 0.5) * 1.5,
        y: y + (Math.random() - 0.5) * 1.5,
        z,
        hue: 210 + Math.random() * 20, // navy/blue range
      })
    }

    let angle = 0
    const startTime = performance.now()
    const fov = 80
    const cx = size / 2
    const cy = size / 2 - 2

    // Realistic heartbeat: "tutum, tutum" (double-beat then pause)
    const heartbeat = (timeMs: number) => {
      const cycleDuration = 830
      const t = (timeMs % cycleDuration) / cycleDuration
      if (t < 0.12) {
        const x = (t / 0.12) * Math.PI
        return Math.pow(Math.sin(x), 0.5)
      }
      if (t < 0.22) return 0
      if (t < 0.32) {
        const x = ((t - 0.22) / 0.1) * Math.PI
        return 0.5 * Math.pow(Math.sin(x), 0.5)
      }
      return 0
    }

    let lastRender = 0
    const render = (now: number) => {
      // Throttle to ~30fps
      if (now - lastRender < 33) {
        animRef.current = requestAnimationFrame(render)
        return
      }
      lastRender = now
      ctx.clearRect(0, 0, size, size)
      angle += 0.01

      const elapsed = now - startTime
      const beatIntensity = heartbeat(elapsed)
      const scale = 1.0 + 0.12 * beatIntensity

      // Glow effect tied to heartbeat
      if (beatIntensity > 0.01) {
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.45)
        grad.addColorStop(0, `rgba(62, 87, 140, ${beatIntensity * 0.4})`)
        grad.addColorStop(1, "rgba(62, 87, 140, 0)")
        ctx.fillStyle = grad
        ctx.fillRect(0, 0, size, size)
      }

      // Project 3D points with rotation
      const projected = points
        .map((p) => {
          const cosA = Math.cos(angle)
          const sinA = Math.sin(angle)
          const rx = p.x * cosA - p.z * sinA
          const rz = p.x * sinA + p.z * cosA
          const s = fov / (fov + rz)
          return {
            sx: cx + rx * scale * s * 1.8,
            sy: cy + p.y * scale * s * 1.8,
            z: rz,
            hue: p.hue,
            s,
          }
        })
        .sort((a, b) => a.z - b.z)

      projected.forEach((p) => {
        const alpha = 0.3 + 0.7 * ((p.z + 5) / 10)
        const radius = Math.max(1, 1.5 * p.s)
        ctx.beginPath()
        ctx.arc(p.sx, p.sy, radius, 0, Math.PI * 2)
        ctx.fillStyle = `hsla(${p.hue}, 80%, 50%, ${Math.min(1, alpha)})`
        ctx.fill()
      })

      // "NaDIA" text centered
      ctx.save()
      ctx.font = "bold 11px sans-serif"
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.shadowColor = "rgba(62, 87, 140, 0.9)"
      ctx.shadowBlur = 8
      ctx.fillStyle = "#3E578C"
      ctx.fillText("NaDIA", cx, cy)
      ctx.restore()

      animRef.current = requestAnimationFrame(render)
    }
    animRef.current = requestAnimationFrame(render)

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current)
    }
  }, [])

  // Auto-show tooltip after 500ms, hide after 5.5s
  useEffect(() => {
    const showTimer = setTimeout(() => setShowTooltip(true), 500)
    const hideTimer = setTimeout(() => {
      setTooltipFaded(true)
      setTimeout(() => setShowTooltip(false), 300)
    }, 5500)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    setShowTooltip(true)
    setTooltipFaded(false)
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current)
  }, [])

  const handleMouseLeave = useCallback(() => {
    tooltipTimerRef.current = setTimeout(() => {
      setTooltipFaded(true)
      setTimeout(() => setShowTooltip(false), 300)
    }, 1500)
  }, [])

  return (
    <div
      onClick={onClick}
      className="fixed bottom-6 right-6 z-[45] cursor-pointer flex flex-col items-center"
      role="button"
      aria-label={t('floatingChat.openAriaLabel')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showTooltip && (
        <div
          className="absolute -top-12 right-0 whitespace-nowrap px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg pointer-events-none"
          style={{
            background: "linear-gradient(135deg, #3E578C, #26375E)",
            color: "white",
            opacity: tooltipFaded ? 0 : 1,
            transform: tooltipFaded ? "translateY(4px)" : "translateY(0)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
          }}
        >
          {t('floatingChat.tooltip')}
          <div
            className="absolute -bottom-1.5 right-6 w-3 h-3 rotate-45"
            style={{ background: "#26375E" }}
          />
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: "block" }} />
    </div>
  )
}

// ─── Chat Panel ─────────────────────────────────────────────────────────────

function ChatPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  // Slide-in animation on mount
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true))
  }, [])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 300) // wait for slide-out animation
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[44] bg-black/20 transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className="fixed bottom-0 right-0 z-[45] h-[80vh] w-full sm:w-[480px] bg-white dark:bg-gray-900 shadow-2xl rounded-tl-2xl flex flex-col transition-transform duration-300 ease-out"
        style={{ transform: visible ? "translateX(0)" : "translateX(100%)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-[#3E578C] to-[#26375E] rounded-tl-2xl">
          <h3 className="text-white font-semibold text-sm tracking-wide">{t('floatingChat.panelTitle')}</h3>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            title={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>
        {/* Chat body — override Chat's viewport-based height to fill panel */}
        <div className="flex-1 overflow-hidden floating-chat-body">
          <Chat />
        </div>
        <style>{`
          .floating-chat-body > div { height: 100%; }
          .floating-chat-body > div > div { height: 100% !important; }
        `}</style>
      </div>
    </>
  )
}
